import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Container, Section, Phone, Clock } from "../components/common";
import { Mail, LogOut, RefreshCw, Lock, Trash2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { fetchLeads, deleteAllLeads, deleteLead } from "../lib/api";
import { useSeo } from "../lib/seo";

const KEY = "rpm_admin_token";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function Leads() {
  useSeo({ title: "Leads Dashboard | RPM Auto Repair", description: "Private leads dashboard.", path: "/leads" });
  const [token, setToken] = useState(() => localStorage.getItem(KEY) || "");
  const [pass, setPass] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const data = await fetchLeads(t);
      setLeads(data.items || []);
      setAuthed(true);
      localStorage.setItem(KEY, t);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Incorrect password.");
        localStorage.removeItem(KEY);
        setAuthed(false);
      } else {
        toast.error("Could not load leads. Try again.");
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (token) load(token); /* eslint-disable-next-line */ }, []);

  const login = (e) => {
    e.preventDefault();
    if (!pass) return;
    setToken(pass);
    load(pass);
  };

  const logout = () => {
    localStorage.removeItem(KEY);
    setToken(""); setPass(""); setAuthed(false); setLeads([]);
  };

  const clearAll = async () => {
    if (!window.confirm(`Delete ALL ${leads.length} leads permanently? This cannot be undone.`)) return;
    try {
      const r = await deleteAllLeads(token);
      toast.success(`Cleared ${r.deleted} lead${r.deleted === 1 ? "" : "s"}.`);
      setLeads([]);
    } catch { toast.error("Could not clear leads."); }
  };

  const removeOne = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await deleteLead(id, token);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    } catch { toast.error("Could not delete lead."); }
  };

  if (!authed) {
    return (
      <Section>
        <Container className="max-w-md">
          <div className="rounded-2xl border border-white/10 bg-steel p-8" data-testid="leads-login">
            <div className="grid h-12 w-12 place-items-center rounded-lg bg-primary/15 text-primary"><Lock size={22} /></div>
            <h1 className="mt-5 font-display text-2xl text-white">Leads Dashboard</h1>
            <p className="mt-2 text-sm text-muted-foreground">Enter your password to view website submissions.</p>
            <form onSubmit={login} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm text-muted-foreground">Password</Label>
                <Input type="password" value={pass} onChange={(e) => setPass(e.target.value)}
                  data-testid="leads-password" className="border-white/15 bg-ink text-white" autoFocus />
              </div>
              <button type="submit" disabled={loading} data-testid="leads-login-btn"
                className="w-full rounded-md bg-primary py-3 font-semibold tracking-wide text-white hover:bg-racing-dark disabled:opacity-60">
                {loading ? "Checking…" : "View Leads"}
              </button>
            </form>
          </div>
        </Container>
      </Section>
    );
  }

  return (
    <Section>
      <Container>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl text-white">Leads</h1>
            <p className="mt-1 text-sm text-muted-foreground">{leads.length} total submission{leads.length === 1 ? "" : "s"}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => load(token)} data-testid="leads-refresh"
              className="flex items-center gap-2 rounded-md border border-white/15 px-4 py-2 text-sm text-white hover:border-primary/50">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            {leads.length > 0 && (
              <button onClick={clearAll} data-testid="leads-clear-all"
                className="flex items-center gap-2 rounded-md border border-primary/40 px-4 py-2 text-sm text-primary hover:bg-primary hover:text-white">
                <Trash2 size={15} /> Clear all
              </button>
            )}
            <button onClick={logout} data-testid="leads-logout"
              className="flex items-center gap-2 rounded-md border border-white/15 px-4 py-2 text-sm text-white hover:border-primary/50">
              <LogOut size={15} /> Log out
            </button>
          </div>
        </div>

        {leads.length === 0 ? (
          <div className="mt-10 rounded-xl border border-white/10 bg-steel p-10 text-center text-muted-foreground">
            No submissions yet.
          </div>
        ) : (
          <div className="mt-8 space-y-4" data-testid="leads-list">
            {leads.map((l, i) => (
              <div key={l.id || i} className="rounded-xl border border-white/10 bg-steel p-5" data-testid="lead-card">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-lg text-white">{l.name}</h3>
                    <span className="mt-1 inline-block rounded-full bg-primary/15 px-3 py-0.5 text-xs font-medium text-primary">
                      {l.service || "General"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Clock size={13} /> {fmtDate(l.createdAt)}
                    </div>
                    <button onClick={() => removeOne(l.id)} data-testid="lead-delete" title="Delete lead"
                      className="grid h-7 w-7 place-items-center rounded-md border border-white/15 text-muted-foreground hover:border-primary/50 hover:text-primary">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
                  <a href={`tel:${l.phone}`} className="flex items-center gap-2 text-white hover:text-primary">
                    <Phone size={15} className="text-primary" /> {l.phone}
                  </a>
                  <a href={`mailto:${l.email}`} className="flex items-center gap-2 text-white hover:text-primary">
                    <Mail size={15} className="text-primary" /> {l.email}
                  </a>
                  {l.vehicle ? <div className="text-muted-foreground">Vehicle: <span className="text-white">{l.vehicle}</span></div> : null}
                  {l.preferredDate ? <div className="text-muted-foreground">Preferred: <span className="text-white">{l.preferredDate}</span></div> : null}
                </div>
                {l.message ? (
                  <p className="mt-3 rounded-lg bg-ink/60 p-3 text-sm leading-relaxed text-muted-foreground">{l.message}</p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Container>
    </Section>
  );
}
